import React, { useState, useEffect } from 'react';
import { Button } from 'src/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from 'src/components/ui/card';
import { Input } from 'src/components/ui/input';
import { Label } from 'src/components/ui/label';
import { Switch } from 'src/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import useEmailIngestConfig from 'src/api/core/organization-config/useEmailIngestConfig';
import useUpdateEmailIngestConfig, {
  UpdateEmailIngestConfigRequest,
} from 'src/api/core/organization-config/useUpdateEmailIngestConfig';
import { useToast } from 'src/context/ToastContext';

const AzureEmailIngestCard: React.FC = () => {
  const {
    data: emailIngestData,
    isLoading: emailIngestLoading,
    error: emailIngestError,
  } = useEmailIngestConfig();
  const updateEmailIngest = useUpdateEmailIngestConfig();
  const { showToast } = useToast();

  const [isEditing, setIsEditing] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingToggleValue, setPendingToggleValue] = useState<boolean | null>(
    null,
  );

  // Azure Email Configuration form state
  const [azureTenantId, setAzureTenantId] = useState('');
  const [azureClientId, setAzureClientId] = useState('');
  const [azureClientSecret, setAzureClientSecret] = useState('');
  const [mailboxUpnsInput, setMailboxUpnsInput] = useState('');

  const emailIngestConfig = emailIngestData?.emailIngestConfig;
  const webhookStatus = emailIngestConfig?.webhookStatus;
  const subscriptionCount = webhookStatus?.subscriptions?.length ?? 0;

  // Initialize form state when data loads or editing starts
  useEffect(() => {
    if (emailIngestConfig && isEditing) {
      setAzureTenantId(emailIngestConfig.tenantId || '');
      setAzureClientId(emailIngestConfig.clientId || '');
      setAzureClientSecret(''); // Never pre-populate secret for security
      setMailboxUpnsInput(emailIngestConfig.mailboxUpns?.join(', ') || '');
    }
  }, [emailIngestConfig, isEditing]);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = async () => {
    try {
      // Parse mailbox UPNs from input
      const parsedUpns = mailboxUpnsInput
        .split(',')
        .map((upn) => upn.trim())
        .filter((upn) => upn.length > 0);

      // Validate mailbox UPNs
      if (parsedUpns.length === 0) {
        showToast({
          message: 'Please provide at least one mailbox UPN',
          color: 'error',
          title: 'Error',
        });
        return;
      }

      // Only validate tenant ID and client ID if they're being changed or if they're not already set
      const isTenantIdChanged =
        azureTenantId.trim() !== (emailIngestConfig?.tenantId || '');
      const isClientIdChanged =
        azureClientId.trim() !== (emailIngestConfig?.clientId || '');
      const isClientSecretProvided = azureClientSecret.trim().length > 0;

      // If we're changing core Azure settings or they're not set yet, validate them
      if (
        isTenantIdChanged ||
        isClientIdChanged ||
        isClientSecretProvided ||
        !emailIngestConfig?.tenantId ||
        !emailIngestConfig?.clientId
      ) {
        // Validate required fields for Azure configuration
        const requiredFields = [
          { value: azureTenantId.trim(), name: 'Tenant ID' },
          { value: azureClientId.trim(), name: 'Client ID' },
        ];

        // Only add client secret validation if it's being changed
        if (isClientSecretProvided || !emailIngestConfig?.hasClientSecret) {
          requiredFields.push({
            value: azureClientSecret.trim(),
            name: 'Client Secret',
          });
        }

        const missingFields = requiredFields.filter((field) => !field.value);
        if (missingFields.length > 0) {
          showToast({
            message: `Please fill in the following required fields: ${missingFields
              .map((f) => f.name)
              .join(', ')}`,
            color: 'error',
            title: 'Error',
          });
          return;
        }

        // Validate GUID format for Tenant ID and Client ID
        const guidRegex =
          /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

        if (!guidRegex.test(azureTenantId.trim())) {
          showToast({
            message:
              'Tenant ID must be a valid GUID format (e.g., 12345678-1234-1234-1234-123456789012)',
            color: 'error',
            title: 'Error',
          });
          return;
        }

        if (!guidRegex.test(azureClientId.trim())) {
          showToast({
            message:
              'Client ID must be a valid GUID format (e.g., 87654321-4321-4321-4321-210987654321)',
            color: 'error',
            title: 'Error',
          });
          return;
        }
      }

      // Validate email format in mailbox UPNs
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const invalidUpns = parsedUpns.filter((upn) => !emailRegex.test(upn));

      if (invalidUpns.length > 0) {
        showToast({
          message: `The following mailbox UPNs are not in valid email format: ${invalidUpns.join(
            ', ',
          )}`,
          color: 'error',
          title: 'Error',
        });
        return;
      }

      // Update email ingest config
      const updatePayload: UpdateEmailIngestConfigRequest = {
        tenantId: azureTenantId.trim() || emailIngestConfig?.tenantId || null,
        clientId: azureClientId.trim() || emailIngestConfig?.clientId || null,
        mailboxUpns: parsedUpns,
      };

      // Only include client secret if it's provided
      if (azureClientSecret.trim()) {
        updatePayload.clientSecret = azureClientSecret.trim();
      }

      await updateEmailIngest.mutateAsync(updatePayload);

      setIsEditing(false);
    } catch (error) {
      console.error('Failed to update email ingest configuration:', error);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const handleToggleEmailIngest = async (enabled: boolean) => {
    if (
      enabled &&
      (!emailIngestConfig?.tenantId ||
        !emailIngestConfig?.clientId ||
        !emailIngestConfig?.hasClientSecret)
    ) {
      showToast({
        message:
          'Please configure all Azure settings before enabling email ingestion.',
        color: 'error',
        title: 'Error',
      });
      return;
    }

    // Show confirmation dialog for enabling
    setPendingToggleValue(enabled);
    setShowConfirmDialog(true);
  };

  const handleConfirmToggle = async () => {
    if (pendingToggleValue === null) return;

    try {
      await updateEmailIngest.mutateAsync({ enabled: pendingToggleValue });
      // Optimistic feel: close immediately; query invalidation will refresh UI
    } catch (error) {
      console.error('Failed to toggle email ingestion:', error);
    } finally {
      setShowConfirmDialog(false);
      setPendingToggleValue(null);
    }
  };

  const handleCancelToggle = () => {
    setShowConfirmDialog(false);
    setPendingToggleValue(null);
  };

  if (emailIngestLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (emailIngestError) {
    return (
      <Card className="border-red-200 bg-red-50">
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold text-red-800 mb-2">
            Error Loading Email Ingest Configuration
          </h3>
          <p className="text-red-600">
            Failed to load email ingest configuration. Please try again.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="shadow-lg border border-gray-200">
      <CardHeader className="pb-4">
        <div className="flex justify-between items-baseline">
          <div className="mb-1">
            <CardTitle
              className="text-xl font-semibold mb-2"
              style={{ color: '#344767', padding: '12px' }}
            >
              Azure Email Ingestion
            </CardTitle>
            <CardDescription
              className="text-sm text-gray-600 leading-relaxed"
              style={{ padding: '12px' }}
            >
              Configure email ingestion from your Azure tenant
            </CardDescription>
          </div>

          {!isEditing ? (
            <Button
              variant="outline"
              size="sm"
              onClick={handleEdit}
              disabled={updateEmailIngest.isLoading}
              className="text-gray-700 border-gray-300 hover:bg-gray-50"
            >
              Edit
            </Button>
          ) : (
            <div className="flex space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleCancel}
                className="text-gray-700 border-gray-300 hover:bg-gray-50"
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleSave}
                disabled={updateEmailIngest.isLoading}
                className="bg-gray-800 hover:bg-gray-900 text-white"
              >
                {updateEmailIngest.isLoading ? 'Saving...' : 'Save'}
              </Button>
            </div>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-6 pt-0" style={{ padding: '12px' }}>
        {isEditing ? (
          <div className="space-y-4">
            <div className="space-y-3">
              <div>
                <Label
                  htmlFor="azure-tenant-id"
                  className="text-sm font-medium"
                  style={{ color: '#344767' }}
                >
                  Tenant ID
                </Label>
                <Input
                  id="azure-tenant-id"
                  type="text"
                  value={azureTenantId}
                  onChange={(e) => setAzureTenantId(e.target.value)}
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                  className="mt-1 border-gray-300 focus:border-gray-500"
                />
              </div>

              <div>
                <Label
                  htmlFor="azure-client-id"
                  className="text-sm font-medium"
                  style={{ color: '#344767' }}
                >
                  Client ID
                </Label>
                <Input
                  id="azure-client-id"
                  type="text"
                  value={azureClientId}
                  onChange={(e) => setAzureClientId(e.target.value)}
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                  className="mt-1 border-gray-300 focus:border-gray-500"
                />
              </div>

              <div>
                <Label
                  htmlFor="azure-client-secret"
                  className="text-sm font-medium"
                  style={{ color: '#344767' }}
                >
                  Client Secret
                </Label>
                <Input
                  id="azure-client-secret"
                  type="text"
                  value={azureClientSecret}
                  onChange={(e) => setAzureClientSecret(e.target.value)}
                  placeholder="Your Azure client secret"
                  className="mt-1 border-gray-300 focus:border-gray-500"
                />
                <p className="text-xs text-gray-600 mt-1">
                  Leave empty to keep current secret unchanged
                </p>
              </div>

              <div>
                <Label
                  htmlFor="azure-mailbox-upns"
                  className="text-sm font-medium"
                  style={{ color: '#344767' }}
                >
                  Mailbox UPNs
                </Label>
                <Input
                  id="azure-mailbox-upns"
                  type="text"
                  value={mailboxUpnsInput}
                  onChange={(e) => setMailboxUpnsInput(e.target.value)}
                  placeholder="user1@domain.com, user2@domain.com"
                  className="mt-1 border-gray-300 focus:border-gray-500"
                />
                <p className="text-xs text-gray-600 mt-1">
                  Comma-separated list of mailbox User Principal Names to
                  monitor
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div
                  className={`w-2 h-2 rounded-full ${
                    emailIngestConfig?.enabled ? 'bg-green-500' : 'bg-gray-300'
                  }`}
                />
                <span className="text-sm text-gray-700">
                  {emailIngestConfig?.enabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>

              {/* Toggle switch for enable/disable */}
              <div className="flex items-center space-x-2">
                <Switch
                  checked={emailIngestConfig?.enabled || false}
                  onCheckedChange={handleToggleEmailIngest}
                  disabled={updateEmailIngest.isLoading}
                />
                <Label className="text-xs text-gray-600">
                  {updateEmailIngest.isLoading ? 'Processing...' : 'Toggle'}
                </Label>
              </div>
            </div>

            <div className="pl-4 space-y-2 text-sm text-gray-600">
              <div>
                <span className="font-medium">Tenant ID:</span>{' '}
                <span className="font-mono text-sm">
                  {emailIngestConfig?.tenantId || 'Not configured'}
                </span>
              </div>
              <div>
                <span className="font-medium">Client ID:</span>{' '}
                <span className="font-mono text-sm">
                  {emailIngestConfig?.clientId || 'Not configured'}
                </span>
              </div>
              <div>
                <span className="font-medium">Client Secret:</span>{' '}
                {emailIngestConfig?.hasClientSecret
                  ? '••••••••'
                  : 'Not configured'}
              </div>
              <div>
                <span className="font-medium">Client Secret Expires:</span>{' '}
                {emailIngestConfig?.clientSecretExpiresAt
                  ? new Date(
                      emailIngestConfig.clientSecretExpiresAt,
                    ).toLocaleDateString()
                  : 'Not specified'}
              </div>
              <div>
                <span className="font-medium">Mailbox UPNs:</span>
                {emailIngestConfig?.mailboxUpns?.length ? (
                  <div className="mt-1">
                    <div className="flex flex-wrap gap-2">
                      {emailIngestConfig.mailboxUpns.map((email, index) => (
                        <span
                          key={index}
                          className="inline-block px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-md font-mono"
                        >
                          {email}
                        </span>
                      ))}
                    </div>
                  </div>
                ) : (
                  <span className="text-gray-500 ml-1">Not configured</span>
                )}
              </div>

              {/* Webhook Status */}
              {emailIngestConfig?.enabled ? (
                <div className="pt-2">
                  <div className="flex items-center space-x-2">
                    <span className="font-medium">Webhook Status:</span>
                    <div
                      className={`w-2 h-2 rounded-full ${
                        webhookStatus?.isActive ? 'bg-green-500' : 'bg-gray-300'
                      }`}
                    />
                    <span className="text-sm">
                      {webhookStatus?.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="text-xs text-gray-600 mt-1">
                    <div>
                      Last checked:{' '}
                      {webhookStatus?.lastChecked
                        ? webhookStatus.lastChecked.toLocaleString()
                        : 'N/A'}
                    </div>
                    <div className="mt-1">
                      Subscriptions: {subscriptionCount}
                    </div>
                    {subscriptionCount ? (
                      <div className="mt-2 space-y-2">
                        {webhookStatus?.subscriptions?.map((sub) => (
                          <div
                            key={sub.subscriptionId}
                            className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 border rounded-md p-2"
                          >
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-xs text-gray-700">
                                {sub.mailboxUpn}
                              </span>
                              <span className="text-[10px] text-gray-500">
                                ID: {sub.subscriptionId}
                              </span>
                            </div>
                            <div className="text-xs text-gray-500">
                              Expires:{' '}
                              {new Date(
                                sub.expirationDateTime,
                              ).toLocaleString()}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        )}
      </CardContent>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {pendingToggleValue
                ? 'Enable Email Ingestion'
                : 'Disable Email Ingestion'}
            </DialogTitle>
            <DialogDescription>
              {pendingToggleValue
                ? 'Enabling email ingestion will set up Azure webhooks and begin processing emails from the configured mailboxes.'
                : 'Disabling email ingestion will stop processing emails from the configured mailboxes.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={handleCancelToggle}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirmToggle}
              disabled={updateEmailIngest.isLoading}
            >
              {updateEmailIngest.isLoading
                ? pendingToggleValue
                  ? 'Enabling...'
                  : 'Disabling...'
                : pendingToggleValue
                  ? 'Enable'
                  : 'Disable'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default AzureEmailIngestCard;
